import { initA11YTabs } from '../../../ts/helper';
import { computePosition, offset, arrow, flip, shift } from '@floating-ui/dom';

/** Selector for any instance of this _Friendation_ component */
const selector = '[is="atom-tariff-calculator"]';

/**
 * Friendation component
 *
 * Initializes the tariff calculator component and
 * binds all interactive features to each instance.
 *
 */
export const atomTariffCalculator = () => {
  /** Instances of this Friendation component */
  const components: HTMLElement[] = Array.from(
    document.querySelectorAll(selector)
  );

  components.forEach((component) => {
    interface DataToSubmit {
      postalCode: string;
      consumption: string;
      mediaType: 'POWER' | 'GAS' | 'POWER_HEATING';
      doubleTariff: 'true' | 'false';
      consumptionNT?: string;
      customerType: 'PRIVATE' | 'BUSINESS';
    }

    /**
     * Handles form submission:
     * - Normalizes form values
     * - Applies double tariff logic
     * - Ensures correct data before submit
     */
    const handleSubmit = () => {
      const forms: HTMLFormElement[] = Array.from(
        component.querySelectorAll('form')
      );

      forms.forEach((form) => {
        form.addEventListener('submit', (e: SubmitEvent) => {
          /** Prevent submit event */
          e.preventDefault();

          /** The form data */
          const formData = new FormData(form);

          const doubleTariffChecked =
            form.elements.namedItem('doubleTariff') instanceof HTMLInputElement
              ? (form.elements.namedItem('doubleTariff') as HTMLInputElement)
                  .checked
              : String(formData.get('doubleTariff')) === 'true';

          const dataToSubmit: DataToSubmit = {
            postalCode: String(formData.get('postalCode') ?? ''),
            consumption: String(formData.get('consumption') ?? ''),
            mediaType: String(
              formData.get('mediaType') ?? 'POWER'
            ) as DataToSubmit['mediaType'],
            doubleTariff: doubleTariffChecked ? 'true' : 'false',
            customerType: String(
              formData.get('customerType') ?? 'PRIVATE'
            ) as DataToSubmit['customerType']
          };
          if (doubleTariffChecked) {
            dataToSubmit.consumptionNT = formData.get('consumptionNT')
              ? String(formData.get('consumptionNT'))
              : '';
          }

          // Update form fields with the values from dataToSubmit
          if (form.elements.namedItem('postalCode')) {
            (form.elements.namedItem('postalCode') as HTMLInputElement).value =
              dataToSubmit.postalCode;
          }
          if (form.elements.namedItem('consumption')) {
            (form.elements.namedItem('consumption') as HTMLInputElement).value =
              dataToSubmit.consumption;
          }
          if (form.elements.namedItem('mediaType')) {
            (form.elements.namedItem('mediaType') as HTMLInputElement).value =
              dataToSubmit.mediaType;
          }
          if (form.elements.namedItem('doubleTariff')) {
            (
              form.elements.namedItem('doubleTariff') as HTMLInputElement
            ).value = dataToSubmit.doubleTariff;
          }
          if (form.elements.namedItem('customerType')) {
            (
              form.elements.namedItem('customerType') as HTMLInputElement
            ).value = dataToSubmit.customerType;
          }
          if (form.elements.namedItem('consumptionNT')) {
            (
              form.elements.namedItem('consumptionNT') as HTMLInputElement
            ).value =
              doubleTariffChecked && dataToSubmit.consumptionNT
                ? dataToSubmit.consumptionNT
                : '';
          }

          form.submit();
        });
      });

      /**
       * Toggles the `required` attribute of the NT consumption field
       * based on the double tariff checkbox state.
       */
      const handleDoubleTariffRequirement = () => {
        const forms = Array.from(component.querySelectorAll('form'));

        forms.forEach((form) => {
          const doubleTariffInput = form.elements.namedItem(
            'doubleTariff'
          ) as HTMLInputElement | null;

          const consumptionNTInput = form.elements.namedItem(
            'consumptionNT'
          ) as HTMLInputElement | null;

          if (!doubleTariffInput || !consumptionNTInput) return;

          const updateRequirement = () => {
            const isChecked = doubleTariffInput.checked;

            consumptionNTInput.toggleAttribute('required', isChecked);

            if (!isChecked) {
              consumptionNTInput.removeAttribute('aria-invalid');
            }
          };

          // Initial state
          updateRequirement();

          // Listen for changes
          doubleTariffInput.addEventListener('change', updateRequirement);
        });
      };

      handleDoubleTariffRequirement();
    };

    const externalSubmitButton: HTMLButtonElement | null =
      component.querySelector('.calculator-submit');

    externalSubmitButton?.addEventListener('click', () => {
      // finde active tabpanel
      const activePanel: HTMLElement = component.querySelector(
        '[role="tabpanel"]:not([hidden])'
      );

      if (!activePanel) return;

      // find active form inside active tabpanel
      const form: HTMLFormElement = activePanel.querySelector('form');

      if (!form) return;

      // submit the form
      form.requestSubmit();
    });

    /**
     * Controls plus/minus inputs:
     * - People count for electricity
     * - Square meter size for gas and power heating
     * - Updates calculated consumption values
     */
    const handleAddSubstract = () => {
      const forms = Array.from(component.querySelectorAll('form'));

      type Mode = 'people' | 'squareMeter';

      const parseNumber = (value: string): number => {
        const parsed = parseInt(value, 10);
        return isNaN(parsed) ? 0 : parsed;
      };

      const formatValue = (value: number, mode: Mode): string => {
        if (mode === 'people') {
          return value === 1 ? '1 Person' : `${value} Personen`;
        }

        return `${value} m²`;
      };

      /**
       * Calculates energy consumption based on square meters
       * using predefined step thresholds.
       */
      const calculateConsumption = (squareMeter: number): number => {
        if (squareMeter <= 30) return 3800;
        if (squareMeter <= 50) return 3800 + (squareMeter - 30) * 60;
        if (squareMeter <= 100) return 5000 + (squareMeter - 50) * 140;
        if (squareMeter <= 150) return 12000 + (squareMeter - 100) * 120;
        return 18000 + (squareMeter - 150) * 100;
      };

      /**
       * Determines the calculation mode
       * based on the control module type.
       */
      const getMode = (module: HTMLElement): Mode => {
        if (module.classList.contains('add-substract-controls--electricity')) {
          return 'people';
        }

        return 'squareMeter';
      };

      forms.forEach((form) => {
        const module = form.querySelector(
          '.add-substract-controls'
        ) as HTMLElement;
        if (!module) return;

        const mode = getMode(module);

        const readOnlyInput = module.querySelector(
          '[name="people"], [name="household"]'
        ) as HTMLInputElement;

        const writeableInput = form.querySelector(
          `#${readOnlyInput.getAttribute('aria-controls')}`
        ) as HTMLInputElement;

        const substractButton = module.querySelector(
          '.substract'
        ) as HTMLButtonElement;
        const addButton = module.querySelector('.add') as HTMLButtonElement;

        const config = {
          people: {
            min: 1,
            max: 6,
            step: 1,
            default: 1
          },
          squareMeter: {
            min: 10,
            max: 2000,
            step: 10,
            default: 50
          }
        }[mode];

        const updateWriteableInput = (value: number) => {
          if (mode === 'people') {
            const prices = [0, 1500, 2400, 3200, 3800, 4200, 5000];
            writeableInput.value = prices[value]?.toString() ?? '';
          } else {
            writeableInput.value = calculateConsumption(value).toString();
          }
        };

        const updateButtons = (value: number) => {
          substractButton.toggleAttribute('disabled', value <= config.min);
          addButton.toggleAttribute('disabled', value >= config.max);
        };

        const setValue = (value: number) => {
          readOnlyInput.value = formatValue(value, mode);
          updateWriteableInput(value);
          updateButtons(value);
        };

        const onAdd = () => {
          const current = parseNumber(readOnlyInput.value);
          if (current >= config.max) return;
          setValue(current + config.step);
        };

        const onSubstract = () => {
          const current = parseNumber(readOnlyInput.value);
          if (current <= config.min) return;
          setValue(current - config.step);
        };

        // Init
        setValue(parseNumber(readOnlyInput.value) || config.default);

        addButton.addEventListener('click', onAdd);
        substractButton.addEventListener('click', onSubstract);
      });
    };

    /**
     * Manages tooltip interactions:
     * - Open/close behavior
     * - Keyboard accessibility
     * - Floating UI positioning
     */
    const handlePopups = () => {
      const triggers = Array.from(
        component.querySelectorAll<HTMLButtonElement>('.tooltip-btn')
      );

      /**
       * ESC closes all tooltips
       */
      const onKeydown = (e: KeyboardEvent) => {
        if (e.code === 'Escape') {
          triggers.forEach((trigger) => hideTooltip(trigger));
        }
      };

      document.addEventListener('keydown', onKeydown);

      /**
       * Find tooltip via aria-controls
       */
      const findTooltip = (trigger: HTMLButtonElement): HTMLElement => {
        return component.querySelector(
          `#${trigger.getAttribute('aria-controls')}`
        ) as HTMLElement;
      };

      /**
       * Show tooltip
       */
      const showTooltip = (trigger: HTMLButtonElement) => {
        const tooltip = findTooltip(trigger);
        const arrowEl = tooltip.querySelector('.arrow') as HTMLElement;

        tooltip.inert = false;
        trigger.setAttribute('aria-expanded', 'true');

        computePosition(trigger, tooltip, {
          placement: 'top',
          middleware: [
            offset(6),
            flip(),
            shift({ padding: 5 }),
            arrow({ element: arrowEl })
          ]
        }).then(({ x, y, placement, middlewareData }) => {
          Object.assign(tooltip.style, {
            left: `${x}px`,
            top: `${y}px`
          });

          const { x: arrowX, y: arrowY } = middlewareData.arrow ?? {};

          const staticSide = {
            top: 'bottom',
            right: 'left',
            bottom: 'top',
            left: 'right'
          }[placement.split('-')[0]];

          Object.assign(arrowEl.style, {
            left: arrowX != null ? `${arrowX}px` : '',
            top: arrowY != null ? `${arrowY}px` : '',
            right: '',
            bottom: '',
            [staticSide]: '-10px'
          });
        });

        /**
         * Click outside → close only this tooltip
         */
        const onOutsideClick = (e: MouseEvent) => {
          const target = e.target as HTMLElement;

          if (!tooltip.contains(target) && !trigger.contains(target)) {
            hideTooltip(trigger);
            document.removeEventListener('click', onOutsideClick);
          }
        };

        // Prevent immediate closing due to opening click
        setTimeout(() => {
          document.addEventListener('click', onOutsideClick);
        });
      };

      /**
       * Hide tooltip
       */
      const hideTooltip = (trigger: HTMLButtonElement, focus = false) => {
        const tooltip = findTooltip(trigger);

        tooltip.inert = true;
        trigger.setAttribute('aria-expanded', 'false');

        if (focus) {
          setTimeout(() => trigger.focus(), 100);
        }
      };

      /**
       * Events pro Trigger
       */
      triggers.forEach((trigger) => {
        const tooltip = findTooltip(trigger);

        let clickCanClose = true;
        const clickTimeout = 1000;

        const onClick = (e: MouseEvent) => {
          e.preventDefault();

          if (tooltip.inert) {
            showTooltip(trigger);
          } else if (clickCanClose) {
            hideTooltip(trigger, true);
          }
        };

        const onMouseEnter = () => {
          if (tooltip.inert) {
            clickCanClose = false;
            showTooltip(trigger);

            setTimeout(() => {
              clickCanClose = true;
            }, clickTimeout);
          }
        };

        trigger.addEventListener('click', onClick);
        trigger.addEventListener('mouseenter', onMouseEnter);
      });
    };

    initA11YTabs(component);

    /**
     * Keeps the "postalCode" input value in sync across all tab panels.
     * Without this, each panel renders its own input and the value would only
     * exist in the currently active panel.
     */
    const handlePostalCodeSync = () => {
      const postalCodeInputs = Array.from(
        component.querySelectorAll<HTMLInputElement>("input[name='postalCode']")
      );

      if (postalCodeInputs.length < 2) return;

      let isSyncing = false;

      const syncFrom = (source: HTMLInputElement) => {
        if (isSyncing) return;
        isSyncing = true;

        const value = source.value;

        postalCodeInputs.forEach((input) => {
          if (input === source) return;

          if (input.value !== value) {
            input.value = value;
            // Trigger potential UI/validation hooks that listen to "input".
            input.dispatchEvent(new Event('input', { bubbles: true }));
          }
        });

        isSyncing = false;
      };

      postalCodeInputs.forEach((input) => {
        input.addEventListener('input', () => syncFrom(input));
        input.addEventListener('change', () => syncFrom(input));
      });
    };

    handlePostalCodeSync();
    handleAddSubstract();
    handleSubmit();
    handlePopups();
  });
};
